// Website-analytics hooks (docs/plans/VENUE_WEBSITE_ANALYTICS_PLAN.md, Phase 2).
//
// The venue-site kit's tracker (packages/venue-site-kit/Layout.astro) exposes
// `window.Embed365Analytics.ids()` → `{ vid, sid }` and listens for `CustomEvent('embed365:track')`.
// Everything here is optional and best-effort: no tracker on the page → no event, no `wa`, nothing thrown.
// `wa` ("web attribution") is `<vid>.<sid>`, two GUIDs; the platform validates it the same way and
// ignores anything else. It is never logged and never placed inside a `return=` value.

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface Tracker { ids?: () => { vid?: unknown; sid?: unknown } | null | undefined }

/** `"<vid>.<sid>"` when the tracker is present and both ids are GUIDs; otherwise null. */
export function trackIds(): string | null {
  try {
    const t = (window as unknown as { Embed365Analytics?: Tracker }).Embed365Analytics;
    const ids = t?.ids?.();
    if (!ids || typeof ids.vid !== 'string' || typeof ids.sid !== 'string') return null;
    if (!GUID.test(ids.vid) || !GUID.test(ids.sid)) return null;
    return `${ids.vid}.${ids.sid}`;
  } catch {
    return null;
  }
}

/** Tell the tracker a funnel step happened (e.g. `booking_start`). No tracker → the event is simply unheard. */
export function track(name: string, widget: string): void {
  try {
    window.dispatchEvent(new CustomEvent('embed365:track', { detail: { name, widget } }));
  } catch { /* ignore */ }
}

/**
 * Append `wa=<vid>.<sid>` to a platform URL's OWN query (never inside an already-encoded `return=`
 * value — call this on the final string, after `return` has been encoded). Unchanged without a tracker.
 */
export function withWa(url: string): string {
  const wa = trackIds();
  if (!wa) return url;
  const hashAt = url.indexOf('#');
  const base = hashAt >= 0 ? url.slice(0, hashAt) : url;
  const hash = hashAt >= 0 ? url.slice(hashAt) : '';
  return `${base}${base.indexOf('?') >= 0 ? '&' : '?'}wa=${wa}${hash}`;
}
